import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  Cross,
  ModalContent,
  Text,
  Space,
  Button,
  List,
  Conditional,
} from '@cashfree-intl/coherent';
import CustomIcon from 'components/CustomIcons';
import { copyToClipboard, roundOffNumber } from 'utils/common';
import { CURRENCY_SYMBOL, PLACEHOLDER_TEXT, WALLET_RECHARGE_ACCOUNT_NAME } from 'utils/constants';

import {
  StyledBankDetails,
  StyledDivider,
  StyledSmallText,
  StyledBankDetailsRow,
  StyledNoteSection,
  StyledInsufficientAmountFooter,
} from './styled';

import InlineLoader from 'components/InlineLoader';
import { InsufficientAmountModalProps } from '../types';

const InsufficientAmountModal = ({ data, walletBalance, closeModal, onRefreshBalance, onConfirmRecharge }: InsufficientAmountModalProps) => {
  const [isLoading, setLoading] = useState(false);

  const onRefresh = () => {
    setLoading(true);
    onRefreshBalance(() => setLoading(false));
  }

  return (<Modal open $maxWidth={480}>
    <ModalHeader>
      Insufficient Amount <Cross
        size="sm"
        onClick={closeModal}
        data-ignore-inner-text="true"
        data-event-name="InsufficientAmountModal_Icon_Close"
      />
    </ModalHeader>
    <ModalContent className="p-0">
      <div className="p-3">
        <Space alignItems="center" justifyContent="space-between">
          <Text variant="h16" color="bodyLight">
            Refund Wallet Balance
          </Text>
          <Space alignItems="center" gap={1}>
            <InlineLoader
              loading={isLoading}
              content={<>
                <Text color="danger" variant="h16" strong>
                  {CURRENCY_SYMBOL.INR} {roundOffNumber(walletBalance || 0)}
                </Text>
                <span
                  data-testid="refresh"
                  data-ignore-inner-text="true"
                  data-event-name="Refresh_Icon"
                  className='pointer'
                  onClick={onRefresh}
                >
                  <CustomIcon componentName="REFRESH" />
                </span>
              </>
              } />
          </Space>
        </Space>
        <StyledDivider className="my-2" />
        <Text variant="b14" strong>
          Recharge your wallet using the details mentioned below.
        </Text>
        <StyledSmallText variant="b12" className="w-500" color="bodyLight">
          Standard TDR charges are applied to add money.
        </StyledSmallText>
        <StyledBankDetails className="mt-2 px-2 pt-2">
          <Text variant="b14" strong>
            Bank Details
          </Text>
          <StyledBankDetailsRow
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="b12" color="bodyLight">
              Account Number
            </Text>
            <Space alignItems="center" gap={1}>
              <Text variant="b12">{data?.vba || PLACEHOLDER_TEXT}</Text>
              <Conditional if={!!data?.vba}>
                <CustomIcon className="pointer" componentName="COPY_TO_CLIPBOARD" handleClick={copyToClipboard(data?.vba)} />
              </Conditional>
            </Space>
          </StyledBankDetailsRow>
          <StyledBankDetailsRow
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="b12" color="bodyLight">
              Account Name
            </Text>
            <Space alignItems="center" gap={1}>
              <Text variant="b12">{WALLET_RECHARGE_ACCOUNT_NAME}</Text>
              <CustomIcon className="pointer" componentName="COPY_TO_CLIPBOARD" handleClick={copyToClipboard(WALLET_RECHARGE_ACCOUNT_NAME)} />
            </Space>
          </StyledBankDetailsRow>
          <StyledBankDetailsRow
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="b12" color="bodyLight">
              IFSC Code
            </Text>
            <Space alignItems="center" gap={1}>
              <Text variant="b12">{data?.ifsc || PLACEHOLDER_TEXT}</Text>
              <Conditional if={!!data?.ifsc}>
                <CustomIcon className="pointer" componentName="COPY_TO_CLIPBOARD" handleClick={copyToClipboard(data?.ifsc)} />
              </Conditional>
            </Space>
          </StyledBankDetailsRow>
        </StyledBankDetails>
        <StyledNoteSection className="my-3">
          <List as="ul">
            <List.Item as="li">
              You won’t be able to recharge using UPI apps like Gpay, Phonepe
              etc.
            </List.Item>
            <List.Item as="li">
              If you are transferring via Axis Bank then should add the
              beneficiary as “Other account” and not “Axis bank account”
            </List.Item>
          </List>
        </StyledNoteSection>
        <Space justifyContent="end">
          <Button
            className="text-right"
            primary
            onClick={onConfirmRecharge}
            data-ignore-inner-text="true"
            data-event-name="Primary_Button_Confirm_Recharge"
          >
            Confirm Recharge
          </Button>
        </Space>
      </div>
      <StyledInsufficientAmountFooter>
        <Text variant="b12" className="w-500" color="bodyLight">
          Feel free to reach out to our support team if you have any questions
          or concerns.
        </Text>
      </StyledInsufficientAmountFooter>
    </ModalContent>
  </Modal>
  );
}

export default InsufficientAmountModal;
